import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowBackIcon } from "../../util/Icons";
import SubMenuWrapper, { SubMenuItem } from "../../util/SubMenu";

const SEDENTARY_MULTIPLIER = 1.2;

const GOAL_OPTIONS = [
  {
    id: "small",
    label: "Lose about 0.5 lb per week",
    calories: 250,
    summary: "About a 250 kcal daily deficit, or roughly 1,750 kcal across the week.",
  },
  {
    id: "moderate",
    label: "Lose about 1 lb per week",
    calories: 500,
    summary: "About a 500 kcal daily deficit, or roughly 3,500 kcal across the week.",
  },
  {
    id: "aggressive",
    label: "Lose about 1.5 lb per week",
    calories: 750,
    summary: "About a 750 kcal daily deficit, or roughly 5,250 kcal across the week.",
    warning: "Not generally recommended",
  },
];

const DEFICIT_COURSE_CARDS = [
  {
    id: 1,
    paragraphs: [
      "To lose weight, you need to create a consistent calorie deficit.",
      "That means your body needs to burn more calories than you eat, over time.",
      "Not through one perfect day.\nNot through eating as little as possible.\nBut through a consistent pattern that you can actually sustain.",
    ],
  },
  {
    id: 2,
    paragraphs: [
      "Do not judge your progress day by day.",
      "Your weight can go up or down from one day to the next for many reasons:",
    ],
    bullets: ["water retention", "sodium intake", "digestion", "menstrual cycle", "soreness from exercise", "stress and sleep"],
    footer: "A single weigh-in does not tell the full story.",
  },
  {
    id: 3,
    paragraphs: [
      "What matters is the trend over time.",
      "If your weight is going down week after week, you are probably doing something right.",
      "If you are not seeing that trend, usually something is off.",
      "Most of the time, it is not because your body is “broken.”",
      "It is because your calorie intake is not being tracked as accurately as you think.",
      "And that is exactly what we need to look at next.",
    ],
  },
  {
    id: 4,
    paragraphs: [
      "You have to be honest with yourself about what you are eating.",
      "If you tell yourself you are in a calorie deficit, but you are not counting everything, nothing is going to work the way it should.",
      "That is not meant to make you feel bad.",
      "It is just the reality of how this works.",
      "The good news: this is a learning process, and you will get better at it over time.",
    ],
  },
  {
    id: 5,
    paragraphs: [
      "This is where a lot of people get surprised.",
      "It is not usually the big meals.\nIt is the little things that do not seem like much, but add up quickly.",
      "Things like:",
    ],
    bullets: ["cooking oil", "dressings", "sauces", "snacks", "drinks", "bites while cooking", "“just a little taste”"],
    footer: "If it has calories, it counts.",
  },
  {
    id: 6,
    paragraphs: [
      "Water matters more than most people think.",
      "A good baseline is at least 2 L of water per day.",
      "And that is before exercise.",
      "So think of it like this:",
    ],
    bullets: ["2 L is your base", "then you add more depending on how much you sweat, walk, train, or how hot it is"],
    footer:
      "If you are active, 2 L is usually not your total. It is your starting point.\n\nGood hydration helps your body function better, recover better, and handle food better.",
  },
  {
    id: 7,
    paragraphs: [
      "The calories from the calculator are your target for losing weight.",
      "Those numbers are already designed to put you in a calorie deficit.",
      "Your job is to stay around that target consistently.",
      "Exercise can help support the process, but the main goal is not to “earn” extra food and accidentally overeat it back.",
      "A better strategy is this:\nStay in your calorie target, stay active, and recover properly.",
    ],
  },
  {
    id: 8,
    paragraphs: [
      "One of the best ways to recover without ruining your deficit is a small post-activity snack.",
      "After activity, even if it was “just a walk,” your body still benefits from some recovery nutrition.",
      "The goal is to give your body enough support to recover and keep your energy up, without eating so much that you erase your deficit.",
      "What we recommend:",
    ],
    bullets: ["20–30 g of protein", "about 150–200 calories"],
    extraTitle: "Examples:",
    extraBullets: [
      "Greek yogurt cup — about 17–20 g protein, 100–140 calories",
      "Protein shake — about 20–30 g protein, 120–180 calories",
      "Cottage cheese — about 20–25 g protein, 150–180 calories",
      "Skyr or high-protein yogurt — about 15–20 g protein, 100–150 calories",
      "Turkey slices + fruit — about 20–25 g protein, 150–200 calories",
    ],
    footer: "Keep it simple. The point is recovery, not turning it into a second meal.",
  },
  {
    id: 9,
    paragraphs: [
      "Your calorie needs change as your body changes.",
      "As you lose weight, your body usually burns fewer calories than it did before.",
      "So the calorie target that worked at the beginning may not keep working forever.",
      "That is why you should:",
    ],
    bullets: ["re-check your progress regularly", "update the calculator about every 4 weeks", "adjust your targets as needed"],
    footer: "Do not just set the number once and assume it stays right forever.",
  },
  {
    id: 10,
    paragraphs: [
      "A calorie deficit is not meant to last forever.",
      "If you stay in a deficit too long, you may start feeling:",
    ],
    bullets: ["more tired", "more hungry", "less recovered", "mentally drained"],
    extraTitle: "A practical approach is:",
    extraBullets: ["re-evaluate your calories every 4 weeks", "after about 12 weeks, consider taking 1 week at maintenance"],
    footer: "That gives your body and mind a chance to recover before starting another fat-loss phase.",
  },
  {
    id: 11,
    paragraphs: [
      "Eating at maintenance for a week is not failure.",
      "It is not “falling off.”",
      "It is not losing progress.",
      "It is a smart reset that can help you recover, feel better, and make the next phase more sustainable.",
    ],
  },
  {
    id: 12,
    paragraphs: ["The goal is not to eat as little as possible.", "The goal is to:"],
    bullets: [
      "create a calorie deficit you can actually sustain",
      "recover well enough to keep going",
      "adjust as your body changes",
      "focus on the trend, not daily noise",
    ],
    footer: "That is how real progress happens.",
  },
  {
    id: 13,
    paragraphs: ["The formula is simple."],
    bullets: [
      "Stay in your calorie target",
      "Track honestly",
      "Drink enough water",
      "Recover with protein",
      "Adjust every 4 weeks",
      "Take breaks when needed",
      "Focus on weekly progress",
    ],
    footer: "That is how you make a calorie deficit work in real life.",
  },
];

function clampCalories(value) {
  return Math.max(1200, Math.round(value));
}

function getMetricWeight(form) {
  if (form.units === "imperial") {
    return Number(form.weightLb || 0) * 0.453592;
  }
  return Number(form.weightKg || 0);
}

function getMetricHeight(form) {
  if (form.units === "imperial") {
    const totalInches = Number(form.heightFt || 0) * 12 + Number(form.heightIn || 0);
    return totalInches * 2.54;
  }
  return Number(form.heightCm || 0);
}

function getBmr({ sex, age, weightKg, heightCm }) {
  if (!age || !weightKg || !heightCm) {
    return 0;
  }

  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return sex === "female" ? base - 161 : base + 5;
}

export default function CalorieDeficitCalculator() {
  const bmrInfoRef = useRef(null);
  const maintenanceInfoRef = useRef(null);
  const courseTouchStartXRef = useRef(0);
  const courseCardRefs = useRef([]);
  const [form, setForm] = useState({
    sex: "male",
    age: 35,
    units: "imperial",
    weightLb: 180,
    weightKg: 82,
    heightFt: 5,
    heightIn: 10,
    heightCm: 178,
  });
  const [isBmrInfoOpen, setIsBmrInfoOpen] = useState(false);
  const [isMaintenanceInfoOpen, setIsMaintenanceInfoOpen] = useState(false);
  const [activeCourseCardIndex, setActiveCourseCardIndex] = useState(0);
  const [activeCourseCardHeight, setActiveCourseCardHeight] = useState(0);

  useEffect(() => {
    if (!isBmrInfoOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (bmrInfoRef.current && !bmrInfoRef.current.contains(event.target)) {
        setIsBmrInfoOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isBmrInfoOpen]);

  useEffect(() => {
    if (!isMaintenanceInfoOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (maintenanceInfoRef.current && !maintenanceInfoRef.current.contains(event.target)) {
        setIsMaintenanceInfoOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isMaintenanceInfoOpen]);

  useEffect(() => {
    function updateActiveCourseCardHeight() {
      const activeCard = courseCardRefs.current[activeCourseCardIndex];
      if (!activeCard) {
        return;
      }

      setActiveCourseCardHeight(activeCard.scrollHeight);
    }

    updateActiveCourseCardHeight();
    window.addEventListener("resize", updateActiveCourseCardHeight);

    return () => {
      window.removeEventListener("resize", updateActiveCourseCardHeight);
    };
  }, [activeCourseCardIndex]);

  const weightKg = getMetricWeight(form);
  const heightCm = getMetricHeight(form);

  const results = useMemo(() => {
    const bmr = getBmr({
      sex: form.sex,
      age: Number(form.age || 0),
      weightKg,
      heightCm,
    });

    if (!bmr) {
      return null;
    }

    const maintenanceCalories = Math.round(bmr * SEDENTARY_MULTIPLIER);

    return {
      bmr: Math.round(bmr),
      maintenanceCalories,
      goals: GOAL_OPTIONS.map((goal) => ({
        ...goal,
        targetCalories: clampCalories(maintenanceCalories - goal.calories),
      })),
    };
  }, [form.sex, form.age, weightKg, heightCm]);

  function updateField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function goToNextCourseCard() {
    setActiveCourseCardIndex((prev) => (prev + 1) % DEFICIT_COURSE_CARDS.length);
  }

  function goToPreviousCourseCard() {
    setActiveCourseCardIndex((prev) => (prev - 1 + DEFICIT_COURSE_CARDS.length) % DEFICIT_COURSE_CARDS.length);
  }

  function handleCourseTouchStart(event) {
    courseTouchStartXRef.current = event.touches[0]?.clientX || 0;
  }

  function handleCourseTouchEnd(event) {
    const touchEndX = event.changedTouches[0]?.clientX || 0;
    const deltaX = touchEndX - courseTouchStartXRef.current;

    if (Math.abs(deltaX) < 40) {
      return;
    }

    if (deltaX < 0) {
      goToNextCourseCard();
      return;
    }

    goToPreviousCourseCard();
  }

  return (
    <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: "#ffffff" }}>
      <div className="max-w-4xl mx-auto">
        <SubMenuWrapper>
          <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Health Hub"} to={"/strong-healthy"} />
        </SubMenuWrapper>

        <div className="text-center mb-6 mt-4">
          <span
            className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-3"
            style={{ background: "linear-gradient(90deg, #006bc1 0%, #0e89ef 100%)", color: "white" }}
          >
            Nutrition Tool
          </span>
          <h1
            className="text-3xl md:text-4xl font-extrabold mb-3"
            style={{
              background: "linear-gradient(90deg, #1e293b 0%, #334155 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Calorie Deficit Calculator
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#64748b" }}>
            Estimate calories to maintain your current weight and choose a sensible daily deficit target.
          </p>
        </div>

        <div className="grid gap-6">
          <div
            className="rounded-3xl p-6 md:p-8"
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #e8f1fb 100%)",
              border: "1px solid rgba(0, 107, 193, 0.14)",
            }}
          >
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Sex">
                <select value={form.sex} onChange={(e) => updateField("sex", e.target.value)} className="w-full rounded-xl border px-3 py-3">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </Field>

              <Field label="Units">
                <div className="flex flex-wrap gap-2">
                  <UnitButton active={form.units === "imperial"} onClick={() => updateField("units", "imperial")}>
                    Imperial
                  </UnitButton>
                  <UnitButton active={form.units === "metric"} onClick={() => updateField("units", "metric")}>
                    Metric
                  </UnitButton>
                </div>
              </Field>

              {form.units === "imperial" ? (
                <Field label="Weight (lb)">
                  <input
                    type="number"
                    min="60"
                    max="600"
                    value={form.weightLb}
                    onChange={(e) => updateField("weightLb", e.target.value)}
                    className="w-full rounded-xl border px-3 py-3"
                  />
                </Field>
              ) : (
                <Field label="Weight (kg)">
                  <input
                    type="number"
                    min="30"
                    max="300"
                    value={form.weightKg}
                    onChange={(e) => updateField("weightKg", e.target.value)}
                    className="w-full rounded-xl border px-3 py-3"
                  />
                </Field>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2 mt-4">
              <Field label="Age">
                <input
                  type="number"
                  min="16"
                  max="100"
                  value={form.age}
                  onChange={(e) => updateField("age", e.target.value)}
                  className="w-full rounded-xl border px-3 py-3"
                />
              </Field>

              {form.units === "imperial" ? (
                <Field label="Height">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      min="3"
                      max="8"
                      value={form.heightFt}
                      onChange={(e) => updateField("heightFt", e.target.value)}
                      className="w-full rounded-xl border px-3 py-3"
                      placeholder="ft"
                    />
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={form.heightIn}
                      onChange={(e) => updateField("heightIn", e.target.value)}
                      className="w-full rounded-xl border px-3 py-3"
                      placeholder="in"
                    />
                  </div>
                </Field>
              ) : (
                <Field label="Height (cm)">
                  <input
                    type="number"
                    min="120"
                    max="250"
                    value={form.heightCm}
                    onChange={(e) => updateField("heightCm", e.target.value)}
                    className="w-full rounded-xl border px-3 py-3"
                  />
                </Field>
              )}
            </div>
          </div>

          <div
            className="rounded-3xl p-6"
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #e8f1fb 100%)",
              border: "1px solid rgba(0, 107, 193, 0.14)",
            }}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: "#1e293b" }}>
              Suggested deficit targets
            </h3>
            <p className="text-sm mb-4" style={{ color: "#64748b" }}>
              Roughly speaking, losing 1 lb of body weight requires about a 3,500 kcal deficit. That is why a 250
              kcal daily deficit is often used for about 0.5 lb per week, and a 500 kcal daily deficit is often used
              for about 1 lb per week. These intake targets are based on the calories to maintain your current weight estimate shown
              above.
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              {results &&
                results.goals.map((goal) => (
                  <div
                    key={goal.id}
                    className="rounded-2xl p-4"
                    style={{ background: "white", border: "1px solid rgba(0, 107, 193, 0.1)" }}
                  >
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <p className="font-semibold m-0" style={{ color: "#1e293b" }}>
                        {goal.label}
                      </p>
                      {goal.warning && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{
                            background: "rgba(249, 115, 22, 0.12)",
                            color: "#c2410c",
                          }}
                        >
                          {goal.warning}
                        </span>
                      )}
                    </div>
                    <p className="text-sm mb-3" style={{ color: "#64748b" }}>
                      {goal.summary}
                    </p>
                    <p className="text-xs mb-1" style={{ color: "#64748b" }}>
                      Daily calorie intake target
                    </p>
                    <p className="text-2xl font-extrabold m-0" style={{ color: "#0b5cab" }}>
                      {goal.targetCalories.toLocaleString()}
                    </p>
                  </div>
                ))}
            </div>
            <p className="text-sm mt-4 mb-0" style={{ color: "#64748b" }}>
              To learn how to manage the calories you burn while working out or doing activities, check the mini-course below.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div
              className="rounded-3xl p-6"
              style={{
                background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                border: "1px solid rgba(0, 107, 193, 0.14)",
              }}
            >
              <div ref={maintenanceInfoRef} className="flex items-center gap-2 mb-2 relative">
                <p className="text-sm font-semibold uppercase tracking-wide m-0" style={{ color: "#64748b" }}>
                  Calories to maintain weight
                </p>
                <button
                  type="button"
                  onClick={() => setIsMaintenanceInfoOpen((prev) => !prev)}
                  className="w-5 h-5 rounded-full border-0 text-xs font-bold cursor-pointer"
                  aria-label="Explain maintenance calories"
                  aria-expanded={isMaintenanceInfoOpen}
                  style={{
                    background: "rgba(100, 116, 139, 0.12)",
                    color: "#475569",
                  }}
                >
                  ?
                </button>
                {isMaintenanceInfoOpen && (
                  <div
                    className="absolute left-0 top-full mt-2 z-10 w-72 rounded-2xl p-4 shadow-lg"
                    style={{
                      background: "#ffffff",
                      border: "1px solid rgba(148, 163, 184, 0.24)",
                    }}
                  >
                    <p className="text-sm font-semibold mb-2" style={{ color: "#1e293b" }}>
                      What maintenance calories mean
                    </p>
                    <p className="text-sm m-0" style={{ color: "#64748b" }}>
                      This is your estimated daily calorie intake for staying at your current weight using a sedentary
                      activity baseline. It combines your BMR with a light day-to-day movement assumption.
                    </p>
                  </div>
                )}
              </div>
              <p className="text-4xl font-extrabold mb-1" style={{ color: "#0b5cab" }}>
                {results ? results.maintenanceCalories.toLocaleString() : "--"}
              </p>
              <p className="text-sm m-0" style={{ color: "#64748b" }}>
                calories per day
              </p>
            </div>

            <div
              className="rounded-3xl p-6"
              style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                border: "1px solid rgba(15, 23, 42, 0.08)",
              }}
            >
              <div ref={bmrInfoRef} className="flex items-center gap-2 mb-2 relative">
                <p className="text-sm font-semibold uppercase tracking-wide m-0" style={{ color: "#64748b" }}>
                  Calories at rest (BMR)
                </p>
                <button
                  type="button"
                  onClick={() => setIsBmrInfoOpen((prev) => !prev)}
                  className="w-5 h-5 rounded-full border-0 text-xs font-bold cursor-pointer"
                  aria-label="Explain BMR"
                  aria-expanded={isBmrInfoOpen}
                  style={{
                    background: "rgba(100, 116, 139, 0.12)",
                    color: "#475569",
                  }}
                >
                  ?
                </button>
                {isBmrInfoOpen && (
                  <div
                    className="absolute left-0 top-full mt-2 z-10 w-72 rounded-2xl p-4 shadow-lg"
                    style={{
                      background: "#ffffff",
                      border: "1px solid rgba(148, 163, 184, 0.24)",
                    }}
                  >
                    <p className="text-sm font-semibold mb-2" style={{ color: "#1e293b" }}>
                      What BMR means
                    </p>
                    <p className="text-sm m-0" style={{ color: "#64748b" }}>
                      BMR stands for Basal Metabolic Rate. It is the calories your body burns at complete rest to keep
                      you alive, including breathing, circulation, and organ function.
                    </p>
                  </div>
                )}
              </div>
              <p className="text-2xl font-extrabold mb-1" style={{ color: "#1e293b" }}>
                {results ? results.bmr.toLocaleString() : "--"}
              </p>
              <p className="text-sm m-0" style={{ color: "#64748b" }}>
                calories per day
              </p>
            </div>
          </div>
        </div>

        <div
          className="rounded-3xl p-6 md:p-7 mt-6 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            border: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <div className="mb-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] mb-1" style={{ color: "#64748b" }}>
              Mini Course
            </p>
            <h3 className="text-lg font-bold m-0" style={{ color: "#1e293b" }}>
              Making a Calorie Deficit Work
            </h3>
            <p className="text-sm mt-2 mb-0" style={{ color: "#64748b" }}>
              A short practical guide to tracking honestly, recovering well, and staying consistent with your deficit over time.
            </p>
          </div>

          <div
            className="overflow-hidden transition-[height] duration-500 ease-out relative"
            style={{ height: activeCourseCardHeight ? `${activeCourseCardHeight}px` : "auto" }}
            onTouchStart={handleCourseTouchStart}
            onTouchEnd={handleCourseTouchEnd}
          >
            <button
              type="button"
              onClick={goToPreviousCourseCard}
              aria-label="Previous card"
              className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full border-0 text-xl font-semibold items-center justify-center"
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                color: "#0b5cab",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
              }}
            >
              ‹
            </button>

            <button
              type="button"
              onClick={goToNextCourseCard}
              aria-label="Next card"
              className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full border-0 text-xl font-semibold items-center justify-center"
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                color: "#0b5cab",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
              }}
            >
              ›
            </button>

            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${activeCourseCardIndex * 100}%)` }}
            >
              {DEFICIT_COURSE_CARDS.map((card) => (
                <div key={card.id} className="min-w-full">
                  <div
                    ref={(node) => {
                      courseCardRefs.current[card.id - 1] = node;
                    }}
                    className="rounded-3xl p-5 md:p-6"
                    style={{
                      background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      border: "1px solid rgba(0, 107, 193, 0.14)",
                    }}
                  >
                    <h4 className="text-lg md:text-xl font-bold mb-3" style={{ color: "#0b5cab" }}>
                      {card.paragraphs[0]}
                    </h4>

                    <div className="grid gap-3">
                      {card.paragraphs.slice(1).map((paragraph, index) => (
                        <p
                          key={`${card.id}-paragraph-${index}`}
                          className="text-sm md:text-base m-0 whitespace-pre-line"
                          style={{ color: "#1e293b", lineHeight: 1.6 }}
                        >
                          {paragraph}
                        </p>
                      ))}

                      {card.bullets && (
                        <ul className="m-0 pl-5 grid gap-1.5 list-disc" style={{ color: "#1e293b" }}>
                          {card.bullets.map((bullet) => (
                            <li key={`${card.id}-bullet-${bullet}`} className="text-sm md:text-base leading-6">
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      )}

                      {card.extraTitle && (
                        <p className="text-sm md:text-base font-semibold m-0" style={{ color: "#1e293b" }}>
                          {card.extraTitle}
                        </p>
                      )}

                      {card.extraBullets && (
                        <ul className="m-0 pl-5 grid gap-1.5 list-disc" style={{ color: "#1e293b" }}>
                          {card.extraBullets.map((bullet) => (
                            <li key={`${card.id}-extra-${bullet}`} className="text-sm md:text-base leading-6">
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      )}

                      {card.footer && (
                        <p className="text-sm md:text-base m-0 whitespace-pre-line" style={{ color: "#1e293b", lineHeight: 1.6 }}>
                          {card.footer}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex md:hidden items-center justify-center gap-3 mt-3">
            <button
              type="button"
              onClick={goToPreviousCourseCard}
              aria-label="Previous card"
              className="w-10 h-10 rounded-full border-0 text-xl font-semibold flex items-center justify-center"
              style={{
                background: "#ffffff",
                color: "#0b5cab",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
              }}
            >
              ‹
            </button>
            <button
              type="button"
              onClick={goToNextCourseCard}
              aria-label="Next card"
              className="w-10 h-10 rounded-full border-0 text-xl font-semibold flex items-center justify-center"
              style={{
                background: "#ffffff",
                color: "#0b5cab",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
              }}
            >
              ›
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            {DEFICIT_COURSE_CARDS.map((card, index) => (
              <button
                key={card.id}
                type="button"
                onClick={() => setActiveCourseCardIndex(index)}
                aria-label={`Go to card ${card.id}`}
                className="border-0 rounded-full transition-all duration-300"
                style={{
                  width: index === activeCourseCardIndex ? 24 : 8,
                  height: 8,
                  background: index === activeCourseCardIndex ? "#0b5cab" : "rgba(148, 163, 184, 0.5)",
                }}
              />
            ))}
          </div>
        </div>

        <div
          className="rounded-3xl p-6 md:p-8 mt-6"
          style={{
            background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
            border: "1px solid rgba(249, 115, 22, 0.16)",
          }}
        >
          <h3 className="text-lg font-bold mb-3" style={{ color: "#9a3412" }}>
            Disclaimer
          </h3>
          <div className="grid gap-2" style={{ color: "#9a3412" }}>
            <p className="text-sm md:text-base m-0">
              This calculator is for general educational purposes only and is not medical, nutrition, or professional advice.
            </p>
            <p className="text-sm md:text-base m-0">
              Not everyone responds the same way to a calorie deficit, so these numbers should be treated as estimates, not guarantees.
            </p>
            <p className="text-sm md:text-base m-0">
              Fatigue, recovery, hunger, sleep, and exercise load should all be monitored closely while using a deficit.
            </p>
            <p className="text-sm md:text-base m-0">
              Consult a qualified professional before attempting to lose weight or starting a calorie deficit.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-2" style={{ color: "#334155" }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function UnitButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 rounded-full border font-semibold transition-colors"
      style={{
        background: active ? "linear-gradient(135deg, #006bc1 0%, #0e89ef 100%)" : "#ffffff",
        color: active ? "white" : "#0f172a",
        borderColor: active ? "transparent" : "rgba(15, 23, 42, 0.12)",
      }}
    >
      {children}
    </button>
  );
}
